import { type } from 'arktype'

import {
  type Amount,
  amountSchema,
  amountToString,
  areAmountsEqual,
  financialRateSchema,
  isAmount,
  isFinancialRate,
  rateToNumber,
  rateToPercentString,
} from '@orus.eu/amount'
import { type Language, type ParameterlessTranslationKey, resolvePolicyText, translate } from '@orus.eu/translations'

import type { InsuranceProductPolicyItemConfigurationResolver } from './policy-item-configuration.ts'

const policyTextValueType = type('string').or(
  type({
    key: 'string',
    parameters: type({ '[string]': 'string' }),
  }),
)

const insuranceOptionalPolicyItem = type("'optional'")
const insuranceIncludedPolicyItem = type("'included'")
const insuranceExcludedPolicyItem = type("'excluded'")

export const insurancePolicyItemOptionalityType = insuranceOptionalPolicyItem
  .or(insuranceIncludedPolicyItem)
  .or(insuranceExcludedPolicyItem)

export type InsurancePolicyItemOptionality = typeof insurancePolicyItemOptionalityType.infer

export const insuranceOptionType = type({
  type: insuranceOptionalPolicyItem,
  isActive: 'boolean',
})
  .or(
    type({
      type: insuranceIncludedPolicyItem,
    }),
  )
  .or(
    type({
      type: insuranceExcludedPolicyItem,
    }),
  )

export type InsuranceOption = typeof insuranceOptionType.infer

export function isInsuranceItemActive(option: InsuranceOption): boolean {
  return option.type === 'included' || (option.type === 'optional' && option.isActive)
}

const possibleSingleLimitOfIndemnityType = type({
  type: "'amounts'",
  amounts: amountSchema.array(),
})
  .or(
    type({
      type: "'fixedAmount'",
      amount: amountSchema,
    }),
  )
  .or(
    type({
      type: "'none'",
    }),
  )
  .or(
    type({
      type: "'percentages'",
      percentages: financialRateSchema.array(),
      baseText: 'string',
    }),
  )
  .or(
    type({
      type: "'timesInMonths'",
      values: type('number').array(),
    }),
  )
  .or(
    type({
      type: "'calculatedAmount'",
    }),
  )
  .or(
    type({
      type: "'text'",
      value: policyTextValueType,
    }),
  )
  .or(
    type({
      type: "'amountPerDuration'",
      amounts: amountSchema.array(),
      durations: type('number').array(),
    }),
  )
  .or(
    type({
      type: "'variableAmount'",
    }),
  )
  .or(
    type({
      type: "'perClaimAndAnnualAmounts'",
    }),
  )
  .or(
    type({
      type: "'variableLimitInText'",
      values: type('string').array(),
    }),
  )

const singleLimitOfIndemnityType = type({
  type: "'amounts'",
  value: amountSchema,
})
  .or(
    type({
      type: "'fixedAmount'",
      value: amountSchema,
    }),
  )
  .or(
    type({
      type: "'none'",
    }),
  )
  .or(
    type({
      type: "'timesInMonths'",
      value: type('number'),
    }),
  )
  .or(
    type({
      type: "'calculatedAmount'",
      value: amountSchema,
    }),
  )
  .or(
    type({
      type: "'text'",
      value: policyTextValueType,
    }),
  )
  .or(
    type({
      type: "'amountPerDuration'",
      amount: amountSchema,
      duration: type('number'),
    }),
  )
  .or(
    type({
      type: "'variableAmount'",
      'value?': amountSchema,
    }),
  )
  .or(
    type({
      type: "'percentages'",
      value: financialRateSchema,
    }),
  )
  .or(
    type({
      type: "'perClaimAndAnnualAmounts'",
      perClaimLimit: amountSchema,
      annualLimit: amountSchema,
    }),
  )
  .or(
    type({
      type: "'variableLimitInText'",
      value: 'string',
    }),
  )

const multipleLimitsType = type({
  type: "'multipleLimits'",
  limits: type({ label: policyTextValueType, limit: singleLimitOfIndemnityType }).array(),
})

export const possibleInsuranceLimitOfIndemnityType = possibleSingleLimitOfIndemnityType.or(multipleLimitsType)

export type PossibleInsuranceLimitOfIndemnity = typeof possibleInsuranceLimitOfIndemnityType.infer

export const insuranceLimitOfIndemnityType = singleLimitOfIndemnityType.or(multipleLimitsType)

export type InsuranceLimitOfIndemnity = typeof insuranceLimitOfIndemnityType.infer

/** One part of a `multipleLimits`, so a product may name and share a list of them. */
export type LimitOfIndemnityPart = Extract<InsuranceLimitOfIndemnity, { type: 'multipleLimits' }>['limits'][number]

export function getInsuranceLimitOfIndemnityText(
  limitOfIndemnity: InsuranceLimitOfIndemnity,
  language: Language = 'fr',
): string {
  switch (limitOfIndemnity.type) {
    case 'fixedAmount':
      return amountToString(limitOfIndemnity.value, { displayDecimals: false, addCurrency: true })
    case 'amounts':
      return amountToString(limitOfIndemnity.value, { displayDecimals: false, addCurrency: true })
    case 'none':
      return translate('none', language)
    case 'calculatedAmount':
      return amountToString(limitOfIndemnity.value, { displayDecimals: false, addCurrency: true })
    case 'text':
      return resolvePolicyText(limitOfIndemnity.value, language)
    case 'variableLimitInText':
      return limitOfIndemnity.value
    case 'timesInMonths':
      return `${limitOfIndemnity.value} ${translate(limitOfIndemnity.value === 1 ? 'month' : 'months', language)}`
    case 'amountPerDuration':
      return `${amountToString(limitOfIndemnity.amount, { displayDecimals: false, addCurrency: true })}/${translate('day', language)} - ${limitOfIndemnity.duration} ${translate(limitOfIndemnity.duration === 1 ? 'month' : 'months', language)}`
    case 'variableAmount':
      return limitOfIndemnity.value
        ? amountToString(limitOfIndemnity.value, { displayDecimals: false, addCurrency: true })
        : ''
    case 'percentages':
      return `${rateToPercentString(limitOfIndemnity.value)}`
    case 'multipleLimits':
      return limitOfIndemnity.limits
        .map(
          ({ label, limit }) =>
            `${resolvePolicyText(label, language)} : ${getInsuranceLimitOfIndemnityText(limit, language)}`,
        )
        .join(' ; ')
    case 'perClaimAndAnnualAmounts':
      return resolvePolicyText(
        {
          key: 'nl_rcph_pi_derived_limit_of_indemnity_text',
          parameters: {
            perClaimAmount: amountToString(limitOfIndemnity.perClaimLimit, {
              displayDecimals: false,
              addCurrency: true,
            }),
            annualAmount: amountToString(limitOfIndemnity.annualLimit, { displayDecimals: false, addCurrency: true }),
          },
        },
        language,
      )
  }
}

/**
 * The parts of a limit the product words as several, each as a label and the text of its own limit. A limit
 * worded as a single one has no part: it already reads as one.
 */
export function getInsuranceLimitOfIndemnityParts(
  limitOfIndemnity: InsuranceLimitOfIndemnity,
  language: Language,
): { name: string; limit: string }[] {
  if (limitOfIndemnity.type !== 'multipleLimits') return []

  return limitOfIndemnity.limits.map(({ label, limit }) => ({
    name: resolvePolicyText(label, language),
    limit: getInsuranceLimitOfIndemnityText(limit, language),
  }))
}

export function getInsuranceLimitOfIndemnityValue(
  limitOfIndemnity: InsuranceLimitOfIndemnity,
  language: Language = 'fr',
): Amount | string {
  switch (limitOfIndemnity.type) {
    case 'fixedAmount':
    case 'amounts':
    case 'calculatedAmount':
      return limitOfIndemnity.value
    case 'none':
      return translate('none', language)
    case 'timesInMonths':
      return `${limitOfIndemnity.value} ${translate(limitOfIndemnity.value === 1 ? 'month' : 'months', language)}`
    case 'text':
      return resolvePolicyText(limitOfIndemnity.value, language)
    case 'variableLimitInText':
      return limitOfIndemnity.value
    case 'amountPerDuration':
      return `${amountToString(limitOfIndemnity.amount, { displayDecimals: false, addCurrency: true })}/${translate('day', language)} - ${limitOfIndemnity.duration} ${translate(limitOfIndemnity.duration === 1 ? 'month' : 'months', language)}`
    case 'variableAmount':
      return limitOfIndemnity.value ?? ''
    case 'percentages':
      return `${rateToPercentString(limitOfIndemnity.value)}`
    case 'multipleLimits':
    case 'perClaimAndAnnualAmounts':
      return getInsuranceLimitOfIndemnityText(limitOfIndemnity, language)
  }
}

export function getInsuranceLimitOfIndemnityAmountValue(
  limitOfIndemnity: InsuranceLimitOfIndemnity,
): Amount | undefined {
  switch (limitOfIndemnity.type) {
    case 'amounts':
    case 'fixedAmount':
    case 'calculatedAmount':
      return limitOfIndemnity.value
    case 'none':
    case 'text':
    case 'variableLimitInText':
    case 'timesInMonths':
    case 'amountPerDuration':
    case 'variableAmount':
    case 'percentages':
    case 'perClaimAndAnnualAmounts':
    case 'multipleLimits':
      return undefined
  }
}

export function getLimitOfIndemnityTitleKey(
  insuranceLimitOfIndemnity: InsuranceLimitOfIndemnity,
): ParameterlessTranslationKey {
  switch (insuranceLimitOfIndemnity.type) {
    case 'timesInMonths':
      return 'limit_of_indemnity_indemnification_period'
    case 'amountPerDuration':
      return 'limit_of_indemnity_daily_coverage_and_duration'
    case 'fixedAmount':
    case 'amounts':
    case 'calculatedAmount':
    case 'text':
    case 'variableLimitInText':
    case 'variableAmount':
    case 'percentages':
    case 'perClaimAndAnnualAmounts':
    case 'multipleLimits':
      return 'limit_of_indemnity'
    case 'none':
      return 'limit_of_indemnity_none'
  }
}

const _insuranceLimitOfIndemnityKeys = insuranceLimitOfIndemnityType.pipe.try((e) => e.type)
export type InsuranceLimitOfIndemnityKey = typeof _insuranceLimitOfIndemnityKeys.infer

const interventionThresholdAmountType = type({
  type: "'interventionThresholdAmount'",
  value: amountSchema,
  'baseText?': policyTextValueType,
})

const interventionThresholdPercentageType = type({
  type: "'interventionThresholdPercentage'",
  value: financialRateSchema,
  baseText: policyTextValueType,
})

const percentageWithMinimumAmountType = type({
  type: "'percentageWithMinimumAmount'",
  value: financialRateSchema,
  minimum: amountSchema,
  baseText: policyTextValueType,
})

const possibleSingleDeductibleType = type({
  type: "'amounts'",
  amounts: amountSchema.array(),
})
  .or(
    type({
      type: "'fixedAmount'",
      amount: amountSchema,
    }),
  )
  .or(type({ type: "'none'" }))
  .or(type({ type: "'not-applicable'" }))
  .or(type({ type: "'variableTimesInDays'", values: type('number').array() }))
  .or(type({ type: "'fixedTimeInDays'", value: type('number') }))
  .or(type({ type: "'calculatedTimeInDays'" }))
  .or(
    type({
      type: "'text'",
      value: 'string',
    }),
  )
  .or(
    type({
      type: "'calculatedAmount'",
    }),
  )
  .or(
    type({
      type: "'variableDeductibleInText'",
      values: type('string').array(),
    }),
  )
  .or(interventionThresholdAmountType)
  .or(interventionThresholdPercentageType)
  .or(percentageWithMinimumAmountType)

const singleDeductibleType = type({
  type: "'amounts'",
  value: amountSchema,
})
  .or(
    type({
      type: "'fixedAmount'",
      value: amountSchema,
    }),
  )
  .or(type({ type: "'none'" }))
  .or(type({ type: "'not-applicable'" }))
  .or(type({ type: "'variableTimesInDays'", value: type('number') }))
  .or(type({ type: "'fixedTimeInDays'", value: type('number') }))
  .or(type({ type: "'calculatedTimeInDays'", value: type('number') }))
  .or(
    type({
      type: "'text'",
      value: 'string',
    }),
  )
  .or(
    type({
      type: "'calculatedAmount'",
      value: amountSchema,
    }),
  )
  .or(
    type({
      type: "'variableDeductibleInText'",
      value: 'string',
    }),
  )
  .or(interventionThresholdAmountType)
  .or(interventionThresholdPercentageType)
  .or(percentageWithMinimumAmountType)

const multipleDeductiblesType = type({
  type: "'multipleDeductibles'",
  deductibles: type({ label: policyTextValueType, deductible: singleDeductibleType }).array(),
})

export const possibleInsuranceDeductibleType = possibleSingleDeductibleType.or(multipleDeductiblesType)

export type PossibleInsuranceDeductible = typeof possibleInsuranceDeductibleType.infer

export const insuranceDeductibleType = singleDeductibleType.or(multipleDeductiblesType)

export type InsuranceDeductible = typeof insuranceDeductibleType.infer

export type DeductiblePart = Extract<InsuranceDeductible, { type: 'multipleDeductibles' }>['deductibles'][number]

export function arePolicyItemDeductiblesEqual(
  deductible1: InsuranceDeductible,
  deductible2: InsuranceDeductible,
): boolean {
  if (deductible1.type !== deductible2.type) {
    return false
  }

  switch (deductible1.type) {
    case 'amounts':
    case 'calculatedAmount':
    case 'fixedAmount':
    case 'interventionThresholdAmount':
      if (!('value' in deductible2) || !isAmount(deductible2.value)) {
        return false
      }
      return areAmountsEqual(deductible1.value, deductible2.value)
    case 'interventionThresholdPercentage':
      if (!('value' in deductible2) || !isFinancialRate(deductible2.value)) {
        return false
      }
      return rateToNumber(deductible1.value) === rateToNumber(deductible2.value)
    case 'percentageWithMinimumAmount':
      if (!('value' in deductible2) || !isFinancialRate(deductible2.value) || !('minimum' in deductible2)) {
        return false
      }
      return (
        rateToNumber(deductible1.value) === rateToNumber(deductible2.value) &&
        areAmountsEqual(deductible1.minimum, deductible2.minimum)
      )
    case 'multipleDeductibles': {
      if (!('deductibles' in deductible2)) {
        return false
      }
      const deductible2Parts = deductible2.deductibles
      return (
        deductible1.deductibles.length === deductible2Parts.length &&
        deductible1.deductibles.every((part, index) => {
          const deductible2Part = deductible2Parts[index]
          return !!deductible2Part && arePolicyItemDeductiblesEqual(part.deductible, deductible2Part.deductible)
        })
      )
    }
    case 'text':
    case 'variableDeductibleInText':
      if (!('value' in deductible2) || typeof deductible2.value !== 'string') {
        return false
      }
      return deductible1.value === deductible2.value
    case 'variableTimesInDays':
    case 'fixedTimeInDays':
    case 'calculatedTimeInDays':
      if (!('value' in deductible2) || typeof deductible2.value !== 'number') {
        return false
      }
      return deductible1.value === deductible2.value
    case 'none':
    case 'not-applicable':
      return true
  }
}

export function getInsuranceDeductibleText(deductible: InsuranceDeductible, language: Language = 'fr'): string {
  switch (deductible.type) {
    case 'fixedAmount':
    case 'amounts':
    case 'calculatedAmount':
      return amountToString(deductible.value, { displayDecimals: false, addCurrency: true })
    case 'none':
      return translate('none_deductible', language)
    case 'variableTimesInDays':
    case 'calculatedTimeInDays':
    case 'fixedTimeInDays':
      return `${deductible.value} ${translate(deductible.value === 1 ? 'day' : 'days', language)}`
    case 'text':
    case 'variableDeductibleInText':
      return deductible.value
    case 'interventionThresholdAmount': {
      const threshold = amountToString(deductible.value, { displayDecimals: false, addCurrency: true })
      return deductible.baseText ? `${threshold} ${resolvePolicyText(deductible.baseText, language)}` : threshold
    }
    case 'interventionThresholdPercentage':
      return `${rateToPercentString(deductible.value)} ${resolvePolicyText(deductible.baseText, language)}`
    case 'percentageWithMinimumAmount':
      return translate('deductible_percentage_with_minimum', language, {
        percentage: rateToPercentString(deductible.value),
        base: resolvePolicyText(deductible.baseText, language),
        minimum: amountToString(deductible.minimum, { displayDecimals: false, addCurrency: true }),
      })
    case 'multipleDeductibles':
      return deductible.deductibles
        .map(
          ({ label, deductible: part }) =>
            `${resolvePolicyText(label, language)} : ${getInsuranceDeductibleText(part, language)}`,
        )
        .join(' ; ')
    case 'not-applicable':
      return ''
  }
}

export function getInsuranceDeductibleParts(
  deductible: InsuranceDeductible,
  language: Language,
): { name: string; deductible: string }[] {
  if (deductible.type !== 'multipleDeductibles') return []

  return deductible.deductibles.map(({ label, deductible: part }) => ({
    name: resolvePolicyText(label, language),
    deductible: getInsuranceDeductibleText(part, language),
  }))
}

export function getInsuranceDeductibleValue(
  deductible: InsuranceDeductible,
  language: Language = 'fr',
): Amount | string {
  switch (deductible.type) {
    case 'fixedAmount':
    case 'amounts':
    case 'calculatedAmount':
      return deductible.value
    case 'none':
      return translate('none_deductible', language)
    case 'variableTimesInDays':
    case 'calculatedTimeInDays':
    case 'fixedTimeInDays':
      return `${deductible.value} ${translate(deductible.value === 1 ? 'day' : 'days', language)}`
    case 'text':
    case 'variableDeductibleInText':
      return deductible.value
    case 'interventionThresholdAmount':
    case 'interventionThresholdPercentage':
    case 'percentageWithMinimumAmount':
    case 'multipleDeductibles':
      return getInsuranceDeductibleText(deductible, language)
    case 'not-applicable':
      return ''
  }
}

export function getInsuranceDeductibleAmountValue(deductible: InsuranceDeductible): Amount | undefined {
  switch (deductible.type) {
    case 'amounts':
    case 'fixedAmount':
    case 'calculatedAmount':
      return deductible.value
    case 'interventionThresholdAmount':
    case 'interventionThresholdPercentage':
    case 'percentageWithMinimumAmount':
    case 'multipleDeductibles':
    case 'none':
    case 'not-applicable':
    case 'variableTimesInDays':
    case 'fixedTimeInDays':
    case 'calculatedTimeInDays':
    case 'text':
    case 'variableDeductibleInText':
      return undefined
  }
}

export function getDeductibleTitleKey(deductible: InsuranceDeductible): ParameterlessTranslationKey {
  switch (deductible.type) {
    case 'interventionThresholdAmount':
    case 'interventionThresholdPercentage':
      return 'deductible_intervention_threshold'
    case 'fixedAmount':
    case 'amounts':
    case 'calculatedAmount':
    case 'multipleDeductibles':
    case 'none':
    case 'percentageWithMinimumAmount':
    case 'variableTimesInDays':
    case 'calculatedTimeInDays':
    case 'fixedTimeInDays':
    case 'text':
    case 'variableDeductibleInText':
    case 'not-applicable':
      return 'deductible'
  }
}

export const policyItemType = type({
  optionality: insuranceOptionType,
  limitOfIndemnity: insuranceLimitOfIndemnityType,
  deductible: insuranceDeductibleType,
})

export type PolicyItem = typeof policyItemType.infer

export function isPolicyItemActive(policyItem: PolicyItem | undefined | null): boolean {
  if (!policyItem || !policyItem.optionality) return false
  return isInsuranceItemActive(policyItem.optionality)
}

export function arePolicyItemsEqual(policyItem1: PolicyItem, policyItem2: PolicyItem): boolean {
  return (
    policyItem1.optionality.type === policyItem2.optionality.type &&
    policyItem1.limitOfIndemnity.type === policyItem2.limitOfIndemnity.type &&
    policyItem1.deductible.type === policyItem2.deductible.type
  )
}

export type InsuranceGuaranteeType = 'guarantee'
export type InsuranceServiceType = 'service'
export type InsuranceFeeType = 'fee'
export type InsuranceLegalType = 'legal'
export type InsuranceImprovementType = 'improvement'
export type InsuranceType =
  | InsuranceGuaranteeType
  | InsuranceServiceType
  | InsuranceFeeType
  | InsuranceLegalType
  | InsuranceImprovementType

export type InsuranceProductVersionAdditionalInfo = {
  limitOfIndemnity: string | null
  deductible: string | null
}

type InsuranceProductPolicyItemLimitOfIndemnity<STATE> =
  | { possibleLimitOfIndemnities: PossibleInsuranceLimitOfIndemnity; selectLimitOfIndemnity?: never }
  | { selectLimitOfIndemnity: (state: STATE) => InsuranceLimitOfIndemnity; possibleLimitOfIndemnities?: never }

type InsuranceProductPolicyItemDeductible<STATE> =
  | { possibleDeductibles: PossibleInsuranceDeductible; selectDeductible?: never }
  | { selectDeductible: (state: STATE) => InsuranceDeductible; possibleDeductibles?: never }

export type InsuranceProductPolicyItem<
  POLICY_ITEMS_NAME extends string,
  STATE extends Record<string, unknown> = Record<string, unknown>,
> = {
  name: POLICY_ITEMS_NAME
  type: InsuranceType
  optionality: InsurancePolicyItemOptionality[]
  dependsOn: POLICY_ITEMS_NAME[]
  editConfiguration: InsuranceProductPolicyItemConfigurationResolver<STATE>
  additionalInfo: InsuranceProductVersionAdditionalInfo | null
} & InsuranceProductPolicyItemLimitOfIndemnity<STATE> &
  InsuranceProductPolicyItemDeductible<STATE>

export type PolicyItems<
  POLICY_ITEMS_NAMES extends string,
  STATE extends Record<string, unknown> = Record<string, unknown>,
> = Record<POLICY_ITEMS_NAMES, InsuranceProductPolicyItem<POLICY_ITEMS_NAMES, STATE>>
